import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { BrandLogo } from "@/components/Brand";
import { AlertTriangle, CheckCircle2, Loader2, CalendarPlus, Wallet } from "lucide-react";

export default function CancelAppointment() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ phase: "loading", data: null, error: "", result: null });
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (!token) { setState({ phase: "error", data: null, error: "Enlace inválido", result: null }); return; }
    api.get(`/appointments/cancel/${token}`)
      .then((r) => setState({ phase: "loaded", data: r.data, error: "", result: null }))
      .catch((e) => setState({ phase: "error", data: null, error: e.response?.data?.detail || "Enlace inválido o expirado", result: null }));
  }, [token]);

  async function doCancel() {
    setCancelling(true);
    try {
      const { data } = await api.post(`/appointments/cancel/${token}`);
      setState((s) => ({ ...s, phase: "cancelled", result: data }));
    } catch (e) {
      setState((s) => ({ ...s, error: e.response?.data?.detail || "No pudimos cancelar", phase: s.data?.status === "cancelled" ? "already" : "error" }));
    } finally { setCancelling(false); }
  }

  const rebookUrl = state.data?.tenant_slug ? `/${state.data.tenant_slug}` : "/";

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-3xl px-6 py-4 flex items-center justify-between">
          <Link to="/"><BrandLogo /></Link>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-6 py-12">
        {state.phase === "loading" && (
          <Card className="border-slate-200"><CardContent className="p-12 text-center">
            <Loader2 className="h-10 w-10 mx-auto text-[#1E3A8A] animate-spin" />
            <p className="text-slate-600 mt-4">Verificando tu cita…</p>
          </CardContent></Card>
        )}

        {state.phase === "error" && (
          <Card className="border-slate-200"><CardContent className="p-12 text-center" data-testid="cancel-error">
            <AlertTriangle className="h-12 w-12 mx-auto text-amber-600" />
            <h1 className="font-display text-3xl font-light mt-6">Enlace inválido</h1>
            <p className="text-slate-600 mt-2">{state.error}</p>
            <Button className="mt-6" onClick={() => navigate("/")}>Ir al inicio</Button>
          </CardContent></Card>
        )}

        {state.phase === "loaded" && state.data && (
          <Card className="border-slate-200" data-testid="cancel-loaded">
            <CardContent className="p-10">
              <p className="overline">Cancelar cita</p>
              <h1 className="font-display text-3xl font-light mt-3">¿Seguro que quieres cancelar?</h1>
              <p className="text-slate-600 mt-2 text-sm">Hola <span className="font-semibold text-slate-900">{state.data.client_name}</span>, esta es tu reserva actual:</p>

              <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-5 space-y-2">
                <div className="flex justify-between text-sm"><span className="text-slate-500">Negocio</span><span className="font-medium">{state.data.tenant_name}</span></div>
                <div className="flex justify-between text-sm"><span className="text-slate-500">Servicio</span><span className="font-medium">{state.data.service_name}</span></div>
                <div className="flex justify-between text-sm"><span className="text-slate-500">Fecha</span><span className="font-medium">{state.data.date} · {state.data.time}</span></div>
                {state.data.deposit_amount > 0 && state.data.payment_status === "paid" && (
                  <div className="flex justify-between text-sm pt-2 border-t border-slate-200"><span className="text-slate-500">Anticipo pagado</span><span className="font-medium text-emerald-700">${state.data.deposit_amount.toFixed(2)} MXN</span></div>
                )}
              </div>

              {state.data.deposit_amount > 0 && state.data.payment_status === "paid" && (
                <div className="mt-6 rounded-xl p-5 text-white" style={{ background: "linear-gradient(135deg, #1E3A8A 0%, #3B82F6 100%)" }} data-testid="cancel-credit-notice">
                  <div className="flex items-center gap-2 opacity-80 text-xs uppercase tracking-wider mb-2">
                    <Wallet className="h-3.5 w-3.5" /> Saldo a favor tras cancelar
                  </div>
                  <p className="font-display text-3xl font-light">${state.data.deposit_amount.toFixed(2)} MXN</p>
                  <p className="text-sm opacity-90 mt-2">Al cancelar, tu anticipo queda como saldo a favor. Cuando reserves nuevamente con <strong>{state.data.client_email}</strong>, lo aplicaremos automáticamente.</p>
                </div>
              )}

              <div className="mt-8 flex flex-wrap gap-3 justify-end">
                <Button variant="outline" onClick={() => navigate(rebookUrl)} data-testid="cancel-keep-btn">Mantener cita</Button>
                <Button onClick={doCancel} disabled={cancelling} className="bg-red-600 hover:bg-red-700" data-testid="cancel-confirm-btn">
                  {cancelling ? "Cancelando…" : "Sí, cancelar cita"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {state.phase === "cancelled" && state.result && (
          <Card className="border-slate-200" data-testid="cancel-success">
            <CardContent className="p-10 text-center">
              <div className="mx-auto h-14 w-14 rounded-full grid place-items-center text-white" style={{ backgroundColor: "#1E3A8A" }}>
                <CheckCircle2 className="h-7 w-7" />
              </div>
              <h1 className="font-display text-3xl font-light mt-6">Cita cancelada</h1>
              <p className="text-slate-600 mt-2">Recibirás un correo confirmando la cancelación.</p>

              {state.result.credit_amount > 0 && (
                <div className="mt-6 rounded-xl p-6 text-white text-left" style={{ background: "linear-gradient(135deg, #1E3A8A 0%, #3B82F6 100%)" }} data-testid="cancel-credit-issued">
                  <p className="overline text-white/70">Saldo a favor</p>
                  <p className="font-display text-4xl font-light mt-2">${state.result.credit_amount.toFixed(2)} MXN</p>
                  <p className="text-sm opacity-90 mt-3">Guardado para tu próxima reserva. Solo usa el mismo correo cuando reserves.</p>
                </div>
              )}

              <div className="mt-8">
                <Button onClick={() => navigate(rebookUrl)} data-testid="cancel-rebook-btn">
                  <CalendarPlus className="h-4 w-4 mr-2" /> Agendar nueva cita
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {state.phase === "already" && (
          <Card className="border-slate-200"><CardContent className="p-10 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto text-amber-600" />
            <h1 className="font-display text-3xl font-light mt-6">Esta cita ya fue cancelada</h1>
            <Button className="mt-6" onClick={() => navigate(rebookUrl)}>Agendar nueva cita</Button>
          </CardContent></Card>
        )}
      </main>
    </div>
  );
}
